import ROUTER from '@/routes/ROUTER'
import IdentityService from '@/shared/services/IdentityService'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { Divider, Form } from 'antd'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import './LoginScreen.scss'

import ForgetModal from '@/components/common/LoginRelatedComponent/component/Forget/ForgetModal'
import VerifyForgetModal from '@/components/common/LoginRelatedComponent/component/Forget/components/VerifyForgotModal'

import CheckboxCustom from '@/components/ui/ui-react-antd/CheckboxCustom'
import InputCustom from '@/components/ui/ui-react-antd/InputCustom'
import CustomModal from '@/components/ui/ui-react-antd/Modal/CustomModal'
import ButtonCustom from '@/components/ui/ui-react-antd/MyButton/Button'
import { MenuItemAdmin } from '@/routes/MenuItem'
import { useAuth, useIsLogin, useRouterContext } from '@/shared/context'
import { useAppDispatch } from '@/shared/redux/hooks'
import { changeAuthorization, setUserInfo } from '@/shared/redux/slices/appGlobal.slice'
import type { LoginRequest } from '@/shared/services/IdentityService/components/identity.param'
import { encryptToken } from '@/shared/utils/crypto'
import { STORAGE, deleteReturnUrl, getReturnUrl, setLocalStorage } from '@/shared/utils/storage'
import { hasPermission } from '@/shared/utils/utils'
import FormLoginSocial from '../../LoginRelatedComponent/component/Login/FormLoginSocial'
import notice from '../../Notice'

const LoginModal = ({ open, onCancel, onLoginSuccess, skipProfileUpdate = false }: any) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [currentModal, setCurrentModal] = useState('login')

  const [emailForReset, setEmailForReset] = useState('')
  const [_codeVerifyForReset, setCodeVerifyForReset] = useState('')

  const { setIsLogin: setIsLoginContext } = useIsLogin()
  const { setRouterBefore: setRouterBeforeLogin } = useRouterContext()
  const { setUser } = useAuth()

  const checkUserNeedsProfileUpdate = useCallback(async () => {
    try {
      const userInfo = await IdentityService.getInfo()
      if (!userInfo || !userInfo.isOk || userInfo.isError) {
        return true
      }
      const userData = userInfo.object
      if (!userData) {
        return true
      }
      return userData.isDetailRequired
    } catch (error) {
      console.error('Error checking user profile:', error)
      return true
    }
  }, [])

  const handleLoginSuccess = useCallback(
    async (userInfo: any, isFirstLogin = false) => {
      handleCloseAllModals()
      if (onLoginSuccess) {
        onLoginSuccess(userInfo)
      }
      if (skipProfileUpdate) {
        navigate(getReturnUrl() || ROUTER.HOME, { replace: true })
        notice({
          msg: 'Đăng nhập thành công!',
          isSuccess: true,
        })
        return
      }
      try {
        const infoResp = await IdentityService.getInfo()
        const userData = infoResp?.object ?? userInfo
        const needsProfileUpdate = userData?.isDetailRequired || isFirstLogin
        const isEmployee = userData?.roles?.some((r: any) => r?.type <= 2)
        const returnUrl = needsProfileUpdate ? null : getReturnUrl()
        if (needsProfileUpdate) {
          deleteReturnUrl()
          navigate('/cap-nhat-thong-tin', { replace: true })
        } else if (returnUrl) {
          navigate(returnUrl, { replace: true })
        } else if (isEmployee) {
          navigate(ROUTER.TONG_QUAN_CUA_TOI, { replace: true })
        } else {
          const comeStartPagePath = await comeStartPage()
          navigate(comeStartPagePath || ROUTER.HOME, { replace: true })
        }
        notice({
          msg: 'Đăng nhập thành công!',
          isSuccess: true,
        })
      } catch (error) {
        console.error('Error in handleLoginSuccess:', error)
        navigate(ROUTER.HOME, { replace: true })
        notice({
          msg: 'Đăng nhập thành công!',
          isSuccess: true,
        })
      }
    },
    [onLoginSuccess, skipProfileUpdate, navigate]
  )

  const handleRegister = (type: string) => {
    handleCloseAllModals()
    // Có thể truyền type để phân biệt cán bộ/nhân viên
    if (type === 'canbo') {
      navigate(ROUTER.DANG_KY_RA_VAO, { state: { accountType: type } })
    } else {
      navigate(ROUTER.DANG_KY_KHACH, { state: { accountType: type } })
    }
  }

  const fetchUserInfo = useCallback(
    async (token: string) => {
      try {
        const response = await IdentityService.getInfo()
        if (response?.isOk) {
          const userInfo = response?.object
          setLocalStorage(STORAGE.USER_INFO, userInfo)
          setUser(userInfo)
          return userInfo
        }
        throw new Error('Failed to fetch user info')
      } catch (error) {
        console.error('Error fetching user info:', error)
        const basicUserInfo = {
          accessToken: token,
          Token: token,
          isProfileCompleted: false,
        }
        setLocalStorage(STORAGE.USER_INFO, basicUserInfo)
        setUser(basicUserInfo)
        return basicUserInfo
      }
    },
    [setUser]
  )

  const setShowListMenu = (list: any, listTab: any) => {
    if (!list?.length) return undefined

    return list
      ?.filter((x: any) => hasPermission(x?.tabid, listTab))
      .map((i: any) => ({
        ...i,
        children: setShowListMenu(i?.children, listTab),
      }))
      .filter((item: any) => {
        if (item.children && item.children.length > 0) {
          return true
        }
        return !item.key?.startsWith('subkey')
      })
  }

  const getListTab = async () => {
    try {
      const resp = await IdentityService.getTabs()
      if (resp.isOk) {
        const filteredMenu = setShowListMenu(MenuItemAdmin(), resp?.object)
        return filteredMenu || []
      }
      return []
    } catch (_error) {
      return []
    } finally {
    }
  }

  const findFirstValidRoute = (menuItems: any[]): any => {
    if (!menuItems || menuItems.length === 0) return null

    for (const item of menuItems) {
      if (item.children && Array.isArray(item.children) && item.children.length > 0) {
        const childRoute: string | null = findFirstValidRoute(item.children)
        if (childRoute) return childRoute
      }

      if (
        (!item.children || !item.children.length) &&
        item.key &&
        typeof item.key === 'string' &&
        !item.key.startsWith('subkey')
      ) {
        return item.key
      }
    }
    return null
  }

  const comeStartPage = async () => {
    const listMenuAdmin = await getListTab()

    if (!listMenuAdmin || listMenuAdmin.length === 0) {
      return ROUTER.HOME
    }
    const firstValidRoute = findFirstValidRoute(listMenuAdmin)
    return firstValidRoute || ROUTER.HOME
  }

  const onFinish = async (values: LoginRequest) => {
    setLoading(true)
    try {
      const res = await IdentityService.login(values)
      if (res?.isOk) {
        const token = res?.object?.accessToken
        const userInfo = res?.object || {}
        setIsLoginContext(token)
        // Mã hoá như setAuthToken/setRefreshToken để getAuthToken/getRefreshToken giải mã đúng
        setLocalStorage(STORAGE.TOKEN, encryptToken(token))
        setLocalStorage(STORAGE.USER_INFO, userInfo)
        setLocalStorage(STORAGE.REFRESH_TOKEN, encryptToken(res?.object?.refreshToken))
        setUser(userInfo)
        dispatch(setUserInfo(userInfo))
        dispatch(changeAuthorization(true))
        setRouterBeforeLogin('')
        await handleLoginSuccess(userInfo, false)
      }
    } catch (error) {
      console.error('Error during login:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenForgetPassModal = (e: React.MouseEvent) => {
    e.preventDefault()
    setCurrentModal('forget')
  }

  const handleOpenVerifyModal = () => {
    setCurrentModal('verify')
  }

  const handleOpenRePasswordModal = () => {
    setCurrentModal('repassword')
  }

  const handleBackToLoginFromReset = () => {
    setCurrentModal('login')
    setEmailForReset('')
    setCodeVerifyForReset('')
  }

  const handleCloseAllModals = () => {
    setCurrentModal('login')
    setEmailForReset('')
    setCodeVerifyForReset('')
    onCancel()
  }

  const handleCancelForgetPassModal = () => {
    handleBackToLoginFromReset()
  }

  const handleCancelVerifyModal = () => {
    setCurrentModal('forget')
  }

  useEffect(() => {
    if (open) {
      setCurrentModal('login')
    }
  }, [open])

  useEffect(() => {
    const handleOAuthRedirect = async () => {
      const hash = location.hash
      if (!hash) {
        return
      }

      const params = new URLSearchParams(hash.substring(1))
      const accessToken = params.get('access_token')
      const refreshToken = params.get('refresh_token')
      const userDetailRequired = params.get('user_detail_required')

      if (accessToken) {
        try {
          setLoading(true)

          setLocalStorage(STORAGE.TOKEN, accessToken)
          if (refreshToken) {
            setLocalStorage(STORAGE.REFRESH_TOKEN, refreshToken)
          }
          setIsLoginContext(accessToken)
          setRouterBeforeLogin('')

          let userInfo: any
          let isFirstLogin = false

          if (userDetailRequired === 'True') {
            userInfo = await fetchUserInfo(accessToken)
            isFirstLogin = true
          } else {
            userInfo = await fetchUserInfo(accessToken)

            // Kiểm tra xem có cần cập nhật profile không
            const needsProfileUpdate = await checkUserNeedsProfileUpdate()
            isFirstLogin = !!needsProfileUpdate
          }

          // Lưu thông tin user
          setLocalStorage(STORAGE.USER_INFO, userInfo)
          setUser(userInfo)
          dispatch(setUserInfo(userInfo))
          dispatch(changeAuthorization(true))

          window.location.hash = ''

          // Gọi handleLoginSuccess
          await handleLoginSuccess(userInfo, isFirstLogin)
        } catch (error) {
          console.error('❌ Error processing OAuth redirect:', error)
        } finally {
          setLoading(false)
        }
      } else {
      }
    }

    handleOAuthRedirect()
  }, [
    location.hash,
    setIsLoginContext,
    setRouterBeforeLogin,
    setUser,
    dispatch,
    fetchUserInfo,
    checkUserNeedsProfileUpdate,
    handleLoginSuccess,
  ])

  return (
    <>
      <CustomModal
        open={open && currentModal === 'login'}
        onCancel={handleCloseAllModals}
        footer={null}
        centered
        width={500}
        className="login-modal-custom"
      >
        <div className="login-form-wrapper">
          <div className="login-form">
            <h2 className="login-title">Đăng nhập hệ thống</h2>

            <Form layout="vertical" onFinish={onFinish} disabled={loading}>
              <Form.Item
                label={
                  <span style={{ color: '#333', fontSize: '14px', fontWeight: 600 }}>
                    Tài khoản
                  </span>
                }
                name="email"
                className="mb-8"
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng nhập tài khoản!',
                  },
                  // {
                  //   // pattern: getRegexEmail(),
                  //   message: 'Email không hợp lệ',
                  // },
                ]}
              >
                <InputCustom
                  size="large"
                  name="username"
                  autoComplete="username"
                  prefix={<UserOutlined />}
                  placeholder="Nhập tên tài khoản"
                />
              </Form.Item>

              <Form.Item
                label={
                  <span style={{ color: '#333', fontSize: '14px', fontWeight: 600 }}>Mật khẩu</span>
                }
                name="password"
                className="mb-8"
                rules={[
                  { required: true, message: 'Vui lòng nhập mật khẩu!' },
                  { min: 6, message: 'Mật khẩu ít nhất 6 ký tự!' },
                ]}
              >
                <InputCustom
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  size="large"
                  prefix={<LockOutlined />}
                  placeholder="Nhập mật khẩu"
                />
              </Form.Item>

              <div className="login-options">
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <CheckboxCustom>Ghi nhớ mật khẩu</CheckboxCustom>
                </Form.Item>
                <ButtonCustom
                  type="link"
                  variant="link"
                  className="forgot-password"
                  style={{
                    padding: 0,
                    height: 'auto',
                    color: '#5C6EBD',
                    fontSize: '14px',
                  }}
                  onClick={handleOpenForgetPassModal}
                >
                  Quên mật khẩu?
                </ButtonCustom>
              </div>

              <Form.Item>
                <ButtonCustom
                  htmlType="submit"
                  size="large"
                  className="login-button-primary"
                  loading={loading}
                >
                  Đăng nhập
                </ButtonCustom>
              </Form.Item>
            </Form>

            <div style={{ width: '100%', padding: '0 10px' }}>
              <Divider
                plain
                style={{
                  margin: '20px 0',
                  color: '#999',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  borderColor: '#e8e8e8',
                }}
              >
                Bạn chưa có tài khoản?
              </Divider>

              <FormLoginSocial />
            </div>

            {/* Phần đăng ký tài khoản trong form */}
            <div
              style={{
                textAlign: 'center',
                color: '#666',
                fontSize: '14px',
              }}
            >
              <div className="registration">
                <div className="registration__links">
                  <div>
                    <ButtonCustom
                      type="link"
                      variant="link"
                      className="registration__link registration__link--member"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault()
                        handleRegister('canbo')
                      }}
                    >
                      Đăng ký tài khoản cán bộ
                    </ButtonCustom>
                  </div>
                  <div>Hoặc</div>
                  <div>
                    <ButtonCustom
                      type="link"
                      variant="link"
                      className="registration__link registration__link--staff"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault()
                        handleRegister('khach')
                      }}
                    >
                      Đăng ký khách hàng
                    </ButtonCustom>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CustomModal>

      <ForgetModal
        openForgetPassModal={open && currentModal === 'forget'}
        handleCancel={handleCancelForgetPassModal}
        handleLogin={handleBackToLoginFromReset}
        setOpenVerifyModal={handleOpenVerifyModal}
        setEmail={setEmailForReset}
      />

      <VerifyForgetModal
        openVerifyModal={open && currentModal === 'verify'}
        handleCancel={handleCancelVerifyModal}
        handleLogin={handleBackToLoginFromReset}
        setRePasswordModal={handleOpenRePasswordModal}
        email={emailForReset}
        setCodeVerify={setCodeVerifyForReset}
      />
    </>
  )
}

export default LoginModal
